/**
 * AttachmentDetailsDialog — shared modal that shows the full details of a
 * single chat attachment.
 *
 * Used from anywhere a file card / chip is clickable.  Renders different
 * bodies based on attachment state:
 *
 *   - failed                  → monospace error + suggestion
 *   - processing/uploading    → spinner + status copy
 *   - ready tabular           → stats bar + per-table schema + sample rows
 *   - ready document          → stats bar + content preview
 *   - low-confidence tabular  → warning banner + "Use this parse" footer action
 *
 * Kept presentation-agnostic so any component (e.g. `FileCard`) can mount it.
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import GridOnRounded from '@mui/icons-material/GridOnRounded';
import ViewColumnRounded from '@mui/icons-material/ViewColumnRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import MenuBookRounded from '@mui/icons-material/MenuBookRounded';
import NotesRounded from '@mui/icons-material/NotesRounded';
import HubRounded from '@mui/icons-material/HubRounded';

import AppDialog from '../dialog/AppDialog';
import type { DraftAttachment, ServerAttachment } from './types';

export type AttachmentVariant =
  | 'uploading'
  | 'queued'
  | 'processing'
  | 'ready-high'
  | 'ready-low-unconfirmed'
  | 'ready-low-confirmed'
  | 'failed';

export function resolveAttachmentVariant(
  draft: DraftAttachment | undefined,
  server: ServerAttachment | undefined,
): AttachmentVariant {
  if (draft && (draft.uploadState === 'queued' || draft.uploadState === 'uploading')) {
    return 'uploading';
  }
  if (draft && draft.uploadState === 'error') {
    return 'failed';
  }
  if (!server) {
    return 'uploading';
  }
  if (server.status === 'pending') return 'queued';
  if (server.status === 'processing') return 'processing';
  if (server.status === 'failed') return 'failed';
  if (server.parse_confidence === 'low') {
    return server.parse_confirmed_at ? 'ready-low-confirmed' : 'ready-low-unconfirmed';
  }
  return 'ready-high';
}

export interface AttachmentDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  server?: ServerAttachment;
  draft?: DraftAttachment;
  /** Called when the user clicks "Use this parse" on a low-confidence file. */
  onConfirm?: () => void;
}

export default function AttachmentDetailsDialog({
  open,
  onClose,
  server,
  draft,
  onConfirm,
}: AttachmentDetailsDialogProps) {
  const variant = resolveAttachmentVariant(draft, server);
  const filename = server?.filename || draft?.file.name || 'file';
  const kind = server?.kind || (draft?.file ? guessKind(draft.file) : 'document');
  const errorMsg = server?.error_message || draft?.uploadError;

  const KindIcon =
    kind === 'tabular' ? TableChartOutlined
      : kind === 'document' ? DescriptionOutlined
        : InsertDriveFileOutlined;

  const headerIconBg =
    variant === 'failed' ? 'error.50'
      : variant === 'ready-low-unconfirmed' ? 'warning.50'
        : kind === 'tabular' ? 'success.50' : 'info.50';
  const headerIconColor =
    variant === 'failed' ? 'error.main'
      : variant === 'ready-low-unconfirmed' ? 'warning.main'
        : kind === 'tabular' ? 'success.main' : 'info.main';

  const showLowConfidenceFooter = variant === 'ready-low-unconfirmed' && Boolean(onConfirm);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={filename}
      subtitle={dialogSubtitle(server, variant)}
      icon={
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: headerIconBg,
            color: headerIconColor,
          }}
        >
          <KindIcon sx={{ fontSize: 20 }} />
        </Box>
      }
      size="md"
      dividers
      contentSx={{ py: 2.5 }}
      {...(showLowConfidenceFooter
        ? {
            primaryAction: {
              label: 'Use this parse',
              color: 'warning' as const,
              onClick: () => { onConfirm?.(); onClose(); },
            },
            secondaryAction: { label: 'Close', onClick: onClose },
          }
        : {
            actions: (
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="text" onClick={onClose}>Close</Button>
              </Box>
            ),
          })}
    >
      <DialogBody
        server={server}
        draft={draft}
        variant={variant}
        errorMsg={errorMsg ?? null}
      />
    </AppDialog>
  );
}

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

function DialogBody({
  server,
  draft,
  variant,
  errorMsg,
}: {
  server?: ServerAttachment;
  draft?: DraftAttachment;
  variant: AttachmentVariant;
  errorMsg: string | null;
}) {
  if (variant === 'failed') {
    return (
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <ErrorRounded color="error" sx={{ fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
            This file couldn't be processed
          </Typography>
        </Stack>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
            fontFamily: '"JetBrains Mono", "Fira Code", monospace',
            fontSize: '0.8125rem',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: 'error.dark',
          }}
        >
          {errorMsg || 'Unknown error.'}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Try re-uploading the file. Make sure it contains clean tabular data with a clear header row.
        </Typography>
      </Box>
    );
  }

  if (variant === 'processing') {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Reading the file and analysing structure…
        </Typography>
      </Stack>
    );
  }

  if (!server) return null;
  if (server.kind === 'tabular') return <TabularBody server={server} variant={variant} />;
  if (server.kind === 'document') return <DocumentBody server={server} />;
  return null;
}

function TabularBody({ server, variant }: { server: ServerAttachment; variant: AttachmentVariant }) {
  const tables = (server.schema_summary?.tables as any[]) || [];
  const warnings: string[] = (server.schema_summary?.warnings as string[]) || [];

  const [activeTab, setActiveTab] = useState(0);

  // Stats tiles reflect the *active* sheet so the user always sees the
  // numbers that match the rows they are currently inspecting.  The
  // "Tables" tile remains a file-level constant.
  const activeIdx = Math.min(activeTab, Math.max(0, tables.length - 1));
  const activeTable = tables[activeIdx];
  const activeRows = activeTable?.row_count ?? 0;
  const activeColumns =
    activeTable?.column_count ?? activeTable?.columns?.length ?? 0;

  return (
    <Box>
      {/* Summary tile row — reflects the active sheet */}
      <Box
        sx={{
          mb: 2.5,
          display: 'grid',
          gridTemplateColumns: `repeat(${server.size_bytes ? 4 : 3}, minmax(0, 1fr))`,
          gap: 1,
        }}
      >
        <SummaryTile
          icon={<GridOnRounded sx={{ fontSize: 16 }} />}
          label="Tables"
          value={tables.length}
        />
        <SummaryTile
          icon={<StorageRounded sx={{ fontSize: 16 }} />}
          label="Rows"
          value={Number(activeRows).toLocaleString()}
        />
        <SummaryTile
          icon={<ViewColumnRounded sx={{ fontSize: 16 }} />}
          label="Columns"
          value={activeColumns || '—'}
        />
        {server.size_bytes ? (
          <SummaryTile
            label="File size"
            value={formatBytes(server.size_bytes)}
          />
        ) : null}
      </Box>

      {/* Low-confidence banner */}
      {variant === 'ready-low-unconfirmed' && (
        <Box
          sx={(t) => ({
            mb: 2.5,
            p: 1.75,
            borderRadius: 1.5,
            bgcolor: alpha(t.palette.warning.main, 0.06),
            border: '1px solid',
            borderColor: alpha(t.palette.warning.main, 0.25),
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
          })}
        >
          <WarningAmberRounded sx={{ fontSize: 18, color: 'warning.main', mt: 0.1, flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
              Low parse confidence
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Review the sample rows below.  If the columns and data look right, click{' '}
              <strong>Use this parse</strong> to let the assistant answer questions about this file.
            </Typography>
          </Box>
        </Box>
      )}

      {warnings.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {warnings.join(' · ')}
        </Typography>
      )}

      {/* Tabs (only when 2+ sheets) */}
      {tables.length > 1 && (
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={(t) => ({
            mb: 2,
            minHeight: 36,
            borderBottom: `1px solid ${t.palette.divider}`,
            '& .MuiTab-root': {
              minHeight: 36,
              textTransform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 500,
              px: 1.5,
              py: 0.75,
            },
            '& .MuiTabs-indicator': { height: 2 },
          })}
        >
          {tables.map((t: any, i: number) => (
            <Tab
              key={i}
              label={
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <span>{t.sheet_name || `Sheet ${i + 1}`}</span>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ fontSize: '0.6875rem', color: 'text.disabled' }}
                  >
                    {(t.row_count ?? 0).toLocaleString()}
                  </Typography>
                </Stack>
              }
            />
          ))}
        </Tabs>
      )}

      {/* Active sheet body */}
      {tables.length > 0 && <SheetBody table={tables[Math.min(activeTab, tables.length - 1)]} />}
    </Box>
  );
}

function SheetBody({ table: t }: { table: any }) {
  const columns = (t.columns || []) as Array<{ name: string; dtype: string }>;
  const previewRows = (t.preview_rows || []) as any[][];
  const visibleColumns = columns.slice(0, 10);
  const extraColumns = Math.max(0, columns.length - 10);
  const hasConfidenceReasons =
    t.confidence === 'low' && (t.confidence_reasons?.length ?? 0) > 0;

  return (
    <Box>
      {/* Per-sheet low-confidence reasons (row/column counts now live in
          the top summary tiles, which switch with the active tab). */}
      {hasConfidenceReasons && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1.5 }}>
          <WarningAmberRounded sx={{ fontSize: 14, color: 'warning.main' }} />
          <Typography variant="caption" color="warning.main">
            {(t.confidence_reasons as string[]).join(' · ')}
          </Typography>
        </Stack>
      )}

      {/* Sample rows table */}
      <TableContainer
        sx={(theme) => ({
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          maxHeight: 320,
          bgcolor: theme.palette.background.paper,
        })}
      >
        <Table size="small" stickyHeader sx={{ minWidth: 400 }}>
          <TableHead>
            <TableRow>
              {visibleColumns.map((c) => (
                <TableCell
                  key={c.name}
                  align={isNumericDtype(c.dtype) ? 'right' : 'left'}
                  sx={(theme) => ({
                    py: 1.25,
                    px: 1.5,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    bgcolor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.04)
                      : alpha(theme.palette.common.black, 0.02),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  })}
                >
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    justifyContent={isNumericDtype(c.dtype) ? 'flex-end' : 'flex-start'}
                  >
                    <span>{c.name}</span>
                    <DtypeBadge dtype={c.dtype} />
                  </Stack>
                </TableCell>
              ))}
              {extraColumns > 0 && (
                <TableCell
                  sx={(theme) => ({
                    py: 1.25,
                    px: 1.5,
                    color: 'text.disabled',
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    bgcolor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.04)
                      : alpha(theme.palette.common.black, 0.02),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  })}
                >
                  +{extraColumns} more
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {previewRows.slice(0, 8).map((row, r) => (
              <TableRow
                key={r}
                sx={(theme) => ({
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.03)
                      : alpha(theme.palette.primary.main, 0.04),
                  },
                  '& td': { borderBottom: `1px solid ${theme.palette.divider}` },
                  '&:last-child td': { borderBottom: 0 },
                })}
              >
                {visibleColumns.map((c, ci) => {
                  const v = row[ci];
                  const isNull = v === null || v === undefined;
                  return (
                    <TableCell
                      key={ci}
                      align={isNumericDtype(c.dtype) ? 'right' : 'left'}
                      sx={{
                        py: 0.875,
                        px: 1.5,
                        fontSize: '0.8125rem',
                        whiteSpace: 'nowrap',
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: isNull ? 'text.disabled' : 'text.primary',
                        fontFamily: isNumericDtype(c.dtype)
                          ? '"JetBrains Mono", "Fira Code", monospace'
                          : undefined,
                      }}
                      title={isNull ? '' : String(v)}
                    >
                      {isNull ? '—' : String(v)}
                    </TableCell>
                  );
                })}
                {extraColumns > 0 && <TableCell />}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {previewRows.length === 0 && (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
          No preview rows available.
        </Typography>
      )}

      {previewRows.length > 0 && (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
          Showing {Math.min(8, previewRows.length)} of {(t.row_count ?? previewRows.length).toLocaleString()} rows.
        </Typography>
      )}
    </Box>
  );
}

function DocumentBody({ server }: { server: ServerAttachment }) {
  return (
    <Box>
      <Box
        sx={{
          mb: 2.5,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 1,
        }}
      >
        <SummaryTile
          icon={<MenuBookRounded sx={{ fontSize: 16 }} />}
          label="Pages"
          value={server.page_count ?? '—'}
        />
        <SummaryTile
          icon={<NotesRounded sx={{ fontSize: 16 }} />}
          label="Characters"
          value={(server.char_count || 0).toLocaleString()}
        />
        <SummaryTile
          icon={<HubRounded sx={{ fontSize: 16 }} />}
          label="Indexed chunks"
          value={server.chunk_count ?? 0}
        />
      </Box>

      {server.preview_text && (
        <>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 0.75,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.secondary',
            }}
          >
            Content preview
          </Typography>
          <Box
            sx={(theme) => ({
              p: 2,
              bgcolor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.03)
                : alpha(theme.palette.common.black, 0.02),
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
              maxHeight: 240,
              overflow: 'auto',
              fontSize: '0.8125rem',
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              color: 'text.secondary',
            })}
          >
            {server.preview_text}
          </Box>
        </>
      )}

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
        The assistant can search this document by meaning — just ask a question about its contents.
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Reusable bits
// ---------------------------------------------------------------------------

function SummaryTile({
  label,
  value,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <Box
      sx={(theme) => ({
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        bgcolor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.03)
          : alpha(theme.palette.common.black, 0.02),
        border: `1px solid ${theme.palette.divider}`,
        minWidth: 0,
      })}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5, color: 'text.secondary' }}>
        {icon}
        <Typography
          variant="caption"
          sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '1rem',
          lineHeight: 1.15,
          color: 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function DtypeBadge({ dtype }: { dtype: string }) {
  const theme = useTheme();
  const palette = dtypePalette(dtype, theme);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.625,
        height: 16,
        borderRadius: 0.75,
        fontSize: '0.625rem',
        fontWeight: 600,
        letterSpacing: 0.2,
        lineHeight: 1,
        textTransform: 'lowercase',
        bgcolor: palette.bg,
        color: palette.fg,
        fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      }}
    >
      {prettyDtype(dtype)}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function dialogSubtitle(
  server: ServerAttachment | undefined,
  variant: AttachmentVariant,
): string | undefined {
  if (!server) return undefined;
  if (variant === 'failed') return 'Processing failed';
  if (variant === 'processing') return 'Analysing structure…';
  if (server.kind === 'tabular') {
    const tables = (server.schema_summary?.tables as any[]) || [];
    const rows = (server.schema_summary?.row_count_total as number)
      ?? tables.reduce((s: number, t: any) => s + (t.row_count || 0), 0);
    return `${tables.length} table${tables.length === 1 ? '' : 's'} · ${rows.toLocaleString()} rows`;
  }
  if (server.kind === 'document') {
    const pages = server.page_count ? `${server.page_count} pages · ` : '';
    return `${pages}${server.chunk_count ?? 0} indexed chunks`;
  }
  return undefined;
}

function guessKind(file: File): 'tabular' | 'document' | 'image' {
  const name = file.name.toLowerCase();
  if (file.type.startsWith('image/')) return 'image';
  if (/\.(csv|tsv|xlsx?|parquet)$/.test(name)) return 'tabular';
  return 'document';
}

function isNumericDtype(dtype: string): boolean {
  if (!dtype) return false;
  const d = dtype.toLowerCase();
  return /(int|float|double|decimal|number|numeric|bigint|smallint)/.test(d);
}

function prettyDtype(dtype: string): string {
  if (!dtype) return 'string';
  const d = dtype.toLowerCase().trim();
  if (/(bigint|int64|int32|int16|int8|smallint|int)/.test(d)) return 'int';
  if (/(float|double|decimal|numeric|number)/.test(d)) return 'num';
  if (/bool/.test(d)) return 'bool';
  if (/(datetime|timestamp)/.test(d)) return 'datetime';
  if (/date/.test(d)) return 'date';
  if (/(string|text|object|varchar|char)/.test(d)) return 'str';
  return d.length > 6 ? d.slice(0, 6) : d;
}

type DtypePalette = { bg: string; fg: string };

function dtypePalette(dtype: string, theme: Theme): DtypePalette {
  const isDark = theme.palette.mode === 'dark';
  const d = (dtype || '').toLowerCase();
  let token: 'num' | 'bool' | 'date' | 'str' = 'str';
  if (isNumericDtype(d)) token = 'num';
  else if (/bool/.test(d)) token = 'bool';
  else if (/(date|datetime|timestamp)/.test(d)) token = 'date';

  const mainByToken = {
    num: theme.palette.info.main,
    bool: theme.palette.secondary.main,
    date: theme.palette.success.main,
    str: theme.palette.text.secondary,
  } as const;
  const main = mainByToken[token];
  return {
    bg: alpha(main, isDark ? 0.18 : 0.1),
    fg: token === 'str'
      ? theme.palette.text.secondary
      : alpha(main, isDark ? 0.95 : 0.85),
  };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
